import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, Activity, Loader2, CheckCircle2, AlertCircle, Wrench,
  RefreshCw, Users, Database,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/**
 * AdminDataSyncTool — diagnoses and heals cross-collection drift between
 * users / creators / vip_creator_requests.
 *
 * The bug this catches: users get promoted to role=creator AFTER signup
 * (via VIP approval, admin role flip), but the creators-collection row
 * that's created in the signup flow doesn't get created on promotion.
 * Dashboard then renders empty because there's no creator profile.
 *
 * Two action paths:
 *   1. "Repair one" — paste an email, fix that single creator
 *   2. "Repair all" — bulk-heal every creator-role user missing a row
 */
export default function AdminDataSyncTool() {
  const [diagnostic, setDiagnostic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  const [bulkRepairing, setBulkRepairing] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [error, setError] = useState('');

  const runDiagnostic = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/api/admin/data-sync-diagnostic`, { headers: auth() });
      setDiagnostic(res.data);
      const issueCount = Object.values(res.data.issues || {}).reduce((sum, arr) => sum + (arr?.length || 0), 0);
      if (issueCount === 0) {
        toast.success('All collections in sync');
      } else {
        toast.warning(`${issueCount} issue${issueCount === 1 ? '' : 's'} detected — see below`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Diagnostic failed');
      toast.error('Diagnostic failed');
    } finally {
      setLoading(false);
    }
  };

  const repairOne = async (promoteIfBuyer = false) => {
    if (!email.trim()) { toast.error('Enter the creator email first'); return; }
    setRepairing(true);
    setError('');
    setRepairResult(null);
    try {
      const res = await axios.post(
        `${API}/api/admin/repair-creator-setup`,
        { email: email.trim(), promote_if_buyer: promoteIfBuyer },
        { headers: auth() },
      );
      setRepairResult(res.data);
      if (res.data.promoted_from_role) {
        toast.success(`Promoted ${res.data.email} from ${res.data.promoted_from_role} → creator and ${res.data.was_missing ? 'created profile row' : 'verified existing profile'}`);
      } else if (res.data.was_missing) {
        toast.success(`Fixed — created missing creator row for ${res.data.email}`);
      } else {
        toast.info(`${res.data.email} already had a creator row — no change needed`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Repair failed';
      setError(msg);
      // If the error was "not creator", auto-suggest the Promote button
      if (msg.includes('promote_if_buyer')) {
        toast.error('That user is a buyer — click "Promote + Repair" instead');
      } else {
        toast.error(msg);
      }
    } finally {
      setRepairing(false);
    }
  };

  const repairAll = async () => {
    if (!window.confirm('Scan EVERY creator-role user and create missing creator rows? Safe + idempotent, but writes new rows for any creator without one.')) {
      return;
    }
    setBulkRepairing(true);
    setError('');
    setBulkResult(null);
    try {
      const res = await axios.post(`${API}/api/admin/repair-all-creators`, {}, { headers: auth() });
      setBulkResult(res.data);
      if (res.data.healed_count === 0) {
        toast.success(`No repair needed — ${res.data.skipped_already_ok} creators already healthy`);
      } else {
        toast.success(`Healed ${res.data.healed_count} creators (${res.data.skipped_already_ok} were already OK)`);
      }
      // Re-run diagnostic to confirm zero issues
      runDiagnostic();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Bulk repair failed');
      toast.error('Bulk repair failed');
    } finally {
      setBulkRepairing(false);
    }
  };

  const issueGroups = diagnostic ? [
    { key: 'creator_users_missing_creators_row', label: 'Creators with NO setup row (Edward bug)', critical: true,
      hint: "These users see 'start over' on their dashboard. Repair fixes it instantly." },
    { key: 'orphan_creators_rows', label: 'Orphan creator rows (no matching user)', critical: false,
      hint: "Creator profile exists but no user record. Usually from old test data." },
    { key: 'approved_vips_never_signed_up', label: 'Approved VIPs who never signed up', critical: false,
      hint: "VIP was approved but they didn't complete signup. May want to resend the setup link." },
    { key: 'vip_flag_without_creator_role', label: 'VIP flag set but role ≠ creator', critical: false,
      hint: "Internal drift — vip_creator=true but role wasn't flipped." },
  ] : [];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-data-sync-tool">
      <header className="border-b border-white/10 sticky top-0 bg-black/95 backdrop-blur z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-neutral-400 hover:text-white inline-flex items-center gap-1 text-sm">
              <ChevronLeft className="w-4 h-4" /> Admin
            </Link>
            <span className="text-neutral-700">|</span>
            <h1 className="font-bold text-lg inline-flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#00F0FF]" />
              Data Sync &amp; Creator Repair
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.04] p-5">
          <h2 className="font-bold text-base mb-1 text-amber-200">Fix "all my setups are missing" reports</h2>
          <p className="text-xs text-neutral-300 leading-relaxed">
            When a buyer is promoted to creator after signup (VIP approval, admin role flip),
            their <code className="text-[#00F0FF]">creators</code> profile row isn't created automatically.
            Their dashboard correctly says "start over" because there's no creator profile to load.
            This page detects and heals that drift across users / creators / vip_creator_requests.
          </p>
          <p className="text-xs text-emerald-300 mt-2 leading-relaxed">
            ✓ Going forward, every login automatically heals this for the user logging in,
            so the issue self-resolves on next signin.
          </p>
        </div>

        {/* Repair one — Edward path */}
        <div className="rounded-xl border border-white/10 bg-neutral-950 p-5 space-y-3" data-testid="repair-one-panel">
          <div>
            <h3 className="font-bold text-sm mb-0.5">Repair one creator by email</h3>
            <p className="text-[11px] text-neutral-400">Paste the email of the creator whose dashboard is broken.</p>
          </div>
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && repairOne(false)}
              placeholder="ek4011@gmail.com"
              data-testid="repair-email-input"
              className="flex-1 px-4 py-2.5 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-neutral-500 focus:border-[#00F0FF]/50 focus:outline-none font-mono"
            />
            <button
              onClick={() => repairOne(false)}
              disabled={repairing}
              data-testid="repair-one-btn"
              className="inline-flex items-center justify-center gap-1.5 px-4 bg-[#00F0FF] text-black font-bold rounded-lg hover:bg-[#00F0FF]/90 disabled:opacity-50 text-sm"
            >
              {repairing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
              Repair
            </button>
            <button
              onClick={() => {
                if (window.confirm(`Promote ${email.trim()} to role=creator AND create their profile row in one step?\n\nUse this when an approved VIP got stuck on role=buyer (Edward Kennedy class).`)) {
                  repairOne(true);
                }
              }}
              disabled={repairing}
              data-testid="promote-and-repair-btn"
              className="inline-flex items-center justify-center gap-1.5 px-4 bg-amber-400/15 border border-amber-400/40 text-amber-200 font-bold rounded-lg hover:bg-amber-400/[0.25] disabled:opacity-50 text-sm"
              title="Flip role to creator + create profile row in one click. Use this for approved VIPs stuck on buyer."
            >
              {repairing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
              Promote + Repair
            </button>
          </div>
          <p className="text-[11px] text-neutral-500 leading-snug">
            <strong className="text-amber-300">Promote + Repair</strong> = use when an approved VIP got stuck on <code>role=buyer</code>.
            Flips role to creator, sets verified flags, and creates the profile row — all in one click. (This is Edward's situation.)
          </p>

          {repairResult && (
            <div
              className={`rounded-lg border p-3 text-xs ${
                repairResult.promoted_from_role
                  ? 'border-amber-400/40 bg-amber-400/[0.06]'
                  : repairResult.was_missing
                  ? 'border-emerald-400/30 bg-emerald-400/[0.04]'
                  : 'border-blue-400/30 bg-blue-400/[0.04]'
              }`}
              data-testid="repair-one-result"
            >
              <div className="flex items-center gap-1.5 mb-1 font-bold">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {repairResult.promoted_from_role
                  ? `Promoted (${repairResult.promoted_from_role} → creator) + ${repairResult.was_missing ? 'created profile' : 'profile verified'}`
                  : repairResult.was_missing ? 'Repaired' : 'Already healthy'}
              </div>
              <div className="text-neutral-300">
                <span className="font-mono">{repairResult.user_id}</span> · {repairResult.email}
              </div>
              {repairResult.creator && (
                <div className="text-neutral-400 mt-1">
                  creator_id: <span className="font-mono text-white">{repairResult.creator.creator_id}</span>
                  {' · '}store_slug: <span className="font-mono text-white">{repairResult.creator.store_slug}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Diagnostic + bulk repair */}
        <div className="rounded-xl border border-white/10 bg-neutral-950 p-5 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-bold text-sm mb-0.5">Cross-collection diagnostic</h3>
              <p className="text-[11px] text-neutral-400">Scan users / creators / vip_requests for drift.</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={runDiagnostic}
                disabled={loading}
                data-testid="run-diagnostic-btn"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-white/[0.06] border border-white/15 text-white font-bold rounded-lg hover:bg-white/[0.10] disabled:opacity-50 text-xs"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Run diagnostic
              </button>
              <button
                onClick={repairAll}
                disabled={bulkRepairing}
                data-testid="repair-all-btn"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-amber-400/15 border border-amber-400/30 text-amber-200 font-bold rounded-lg hover:bg-amber-400/[0.20] disabled:opacity-50 text-xs"
              >
                {bulkRepairing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5" />}
                Repair all creators
              </button>
            </div>
          </div>

          {diagnostic && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(diagnostic.totals).map(([k, v]) => (
                  <div key={k} className="rounded-lg bg-black/30 border border-white/10 p-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-0.5">
                      <Database className="inline w-3 h-3 mr-0.5" />
                      {k.replace(/_/g, ' ')}
                    </div>
                    <div className="text-white font-mono text-lg">{v}</div>
                  </div>
                ))}
              </div>

              <div className="rounded-lg bg-black/30 border border-white/10 p-2.5">
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-1.5">
                  <Users className="inline w-3 h-3 mr-0.5" />
                  Role breakdown
                </div>
                <div className="flex gap-3 flex-wrap text-xs">
                  {Object.entries(diagnostic.role_breakdown || {}).map(([role, n]) => (
                    <span key={role} className="inline-flex items-center gap-1.5">
                      <span className="text-neutral-400">{role}:</span>
                      <span className="text-white font-mono">{n}</span>
                    </span>
                  ))}
                </div>
              </div>

              <div className="space-y-2 pt-2 border-t border-white/10">
                {issueGroups.map((g) => {
                  const items = diagnostic.issues[g.key] || [];
                  const has = items.length > 0;
                  return (
                    <div
                      key={g.key}
                      className={`rounded-lg border p-3 ${
                        has && g.critical ? 'border-red-400/30 bg-red-400/[0.04]' :
                        has ? 'border-amber-400/20 bg-amber-400/[0.03]' :
                        'border-emerald-400/20 bg-emerald-400/[0.04]'
                      }`}
                      data-testid={`issue-group-${g.key}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-semibold text-sm flex items-center gap-1.5">
                          {has ? <AlertCircle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                          {g.label}
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                          has && g.critical ? 'bg-red-400/15 text-red-200 border border-red-400/30' :
                          has ? 'bg-amber-400/15 text-amber-200 border border-amber-400/30' :
                          'bg-emerald-400/15 text-emerald-200 border border-emerald-400/30'
                        }`}>
                          {items.length}
                        </span>
                      </div>
                      <div className="text-[11px] text-neutral-400 mb-2 leading-snug">{g.hint}</div>
                      {has && (
                        <div className="space-y-1 max-h-48 overflow-auto">
                          {items.slice(0, 20).map((it, idx) => (
                            <div key={idx} className="text-[11px] text-neutral-300 flex gap-2 items-center bg-black/30 rounded px-2 py-1">
                              <span className="font-mono text-white">
                                {it.user_id || it.creator_id || it.request_id || '?'}
                              </span>
                              <span className="truncate">{it.email || ''}</span>
                              {it.name && <span className="text-neutral-500">· {it.name}</span>}
                              {it.role && <span className="text-amber-300">role={it.role}</span>}
                              {it.reason && <span className="text-red-300">{it.reason}</span>}
                            </div>
                          ))}
                          {items.length > 20 && (
                            <div className="text-[11px] text-neutral-500 italic">… and {items.length - 20} more</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {bulkResult && (
            <div className="rounded-lg border border-emerald-400/30 bg-emerald-400/[0.04] p-3 text-xs" data-testid="bulk-repair-result">
              <div className="font-bold text-emerald-200 mb-1">
                <CheckCircle2 className="inline w-3.5 h-3.5 mr-1" />
                Bulk repair complete
              </div>
              <div className="text-neutral-300">
                Healed: <span className="text-white font-mono">{bulkResult.healed_count}</span>
                {' · '}Already OK: <span className="text-white font-mono">{bulkResult.skipped_already_ok}</span>
              </div>
              {bulkResult.healed.length > 0 && (
                <div className="mt-2 space-y-1 max-h-48 overflow-auto">
                  {bulkResult.healed.map((h) => (
                    <div key={h.user_id} className="text-[11px] text-neutral-300 bg-black/30 rounded px-2 py-1 flex gap-2 truncate">
                      <span className="font-mono text-white">{h.user_id}</span>
                      <span className="truncate">{h.email}</span>
                      <span className="text-emerald-300 ml-auto">→ {h.new_store_slug}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-xl border border-red-400/30 bg-red-400/[0.04] p-5" data-testid="sync-error">
            <div className="flex items-center gap-2 mb-1 text-red-300 font-bold text-sm">
              <AlertCircle className="w-4 h-4" />
              Error
            </div>
            <div className="text-xs text-red-200/80">{error}</div>
          </div>
        )}
      </main>
    </div>
  );
}
